import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Row, Spinner } from "react-bootstrap";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { ordersActions } from "../../../_store/order.slice";
import { QuickWeighedWasteTable } from "./QuickWeighedWasteTable";
import { DomainButton } from "../../Abstractions/DomainButton";
import { SelectTable } from "../../Abstractions/SelectTable";
import { BadgeDomain } from "../../Abstractions/BadgeDomain";
import { stringToDate } from "../../../domain/Configuration";
import { useToast } from "../../../_helpers/notifications/useToasts";
import { WastesKindsSelect } from "../../../useCases/contractors/Extensions/CreateContractor/WastesKindsSelect";

export function QuickWeighedWasteSelect({ wasteFieldName }) {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [assignedItems, setAssignedItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showErrorMessage } = useToast();
  const { getValues, setValue } = useFormContext();

  const fetchItems = async (searchQuery) => {
    setLoading(true);
    try {
      const { payload, error } = await dispatch(ordersActions.getQuickWeighedWastes({ searchQuery }));
      if (error) {
        showErrorMessage(error.message);
        return;
      }

      setItems(payload ?? []);
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = () => {
    setAssignedItems(selectedItems);
  };

  const handleAssignSubmit = (values) => {
    const wastesFieldName = `${wasteFieldName}.wastes`;
    const currentWastes = getValues(wastesFieldName) ?? [];

    const newWastes = values.selections.map((selection) => {
      const waste = selection.waste;
      return {
        wasteId: waste?.wasteId,
        wasteKindId: waste?.wasteKindId,
        wasteEntityId: waste?.wasteEntityId,
        displayDescription: waste?.displayDescription,
        wasteCode: waste?.wasteCode,
        wasteMass: (selection.weight / 1000).toFixed(4),
        wasteFormType: null,
        externalWeighedWasteId: selection.id,
        isQuickWeighed: true,
        externalWeighedWasteDescription: `${selection.id} (SW)`
      };
    });

    setValue(wastesFieldName, [...currentWastes, ...newWastes], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });

    handleClose();
  };

  const handleClose = () => {
    setShow(false);
    setSelectedItems([]);
    setAssignedItems(null);
    setItems([]);
  };

  const handleSetShow = (value) => {
    if (!value) {
      handleClose();
      return;
    }

    setShow(true);
  };

  const selectContent = (
    <div className="d-flex align-items-center gap-3 px-3 pb-2">
      {selectedItems.length > 0 && (
        <BadgeDomain>Wybrano: {selectedItems.length}</BadgeDomain>
      )}
      <DomainButton
        onClick={handleProceed}
        disabled={selectedItems.length === 0}
        className="ms-auto"
      >
        Dalej - przypisz towary
      </DomainButton>
    </div>
  );

  const table = loading
    ? <div className="d-flex justify-content-center py-4"><Spinner /></div>
    : (
      <QuickWeighedWasteTable
        items={items}
        selectedItems={selectedItems}
        onSelectedItemsChanged={setSelectedItems}
      />
    );

  useEffect(() => {
    if (show) {
      fetchItems();
    }
  }, [show]); // eslint-disable-next-line/react-hooks/exhaustive-deps

  return (
    <SelectTable
      table={assignedItems
        ? <QuickWeighedWasteAssignForm items={assignedItems} onSubmit={handleAssignSubmit} onBack={() => setAssignedItems(null)} />
        : table}
      label="Szybkie ważenia"
      selectContent={assignedItems ? null : selectContent}
      show={show}
      onSearchChange={fetchItems}
      setShow={handleSetShow}
      //skipSearchBar
      btnSize="small"
      btnVariant="primary"
      modalTitle="Szybkie ważenia"
    />
  );
}

function QuickWeighedWasteAssignForm({ items, onSubmit, onBack }) {
  const methods = useForm({
    defaultValues: {
      selections: items.map((item) => ({
        id: item.id,
        date: item.date,
        weight: item.weight,
        waste: null
      }))
    }
  });
  const { handleSubmit, formState: { errors } } = methods;

  const submitHandler = handleSubmit(onSubmit);
  const handleClick = async (e) => {
    // Nested <form> elements are invalid HTML - the browser flattens them,
    // so a native submit here would bubble up and submit/close the parent form.
    // Using a plain div + button click (with preventDefault/stopPropagation) avoids that.
    e.preventDefault();
    e.stopPropagation();
    await submitHandler(e);
  };

  return (
    <FormProvider {...methods}>
      <div>
        {items.map((item, index) => (
          <QuickWeighedWasteAssignRow key={item.id} index={index} item={item} />
        ))}
        <Row className="d-flex align-items-center gap-2 px-3 pb-2 mt-2">
          <Button variant="outline-secondary" className="w-auto" onClick={onBack}>Wróć</Button>
          <DomainButton type="button" className="w-auto ms-auto" onClick={handleClick}>Dodaj do listy towarów</DomainButton>
        </Row>
        {errors.selections && <div className="text-danger px-3">Uzupełnij towar dla każdej pozycji</div>}
      </div>
    </FormProvider>
  );
}

function QuickWeighedWasteAssignRow({ index, item }) {
  const { register } = useFormContext();

  return (
    <div className="d-flex align-items-start gap-3 px-3 py-2 border-bottom">
      <div style={{ minWidth: "220px" }}>
        <div>Data ważenia: {stringToDate(item.date)}</div>
        <div>Masa: {item.weight} kg</div>
      </div>
      <div className="flex-grow-1">
        <input type="hidden" {...register(`selections[${index}].id`)} />
        <input type="hidden" {...register(`selections[${index}].weight`)} />
        <WastesKindsSelect name={`selections[${index}].waste`} label="Towar" required />
      </div>
    </div>
  );
}


